/**
 * Product Domain Bridge Session API client.
 * Uses {@link ./driveFlowAdapter} — main `apiClient` by default (ISSUE-223), or dedicated Local Bridge when
 * `REACT_APP_DRIVE_USE_DEDICATED_LOCAL_BRIDGE=true`.
 */
import {
  getDriveFlowHttpClient,
  getProductDomainSessionsBasePath,
} from './driveFlowAdapter';
import type { AxiosInstance } from 'axios';
import type { ProductDomainConfig } from '../types/productDomain';
import type { PhaseDataItem } from '../types/productDomain';

export interface ProductDomainSession {
  id: string;
  company_id: string;
  user_id: string;
  objective_id?: string | null;
  bridge_session_id?: string | null;
  product_revenue: number;
  product_expense: number;
  product_state: string;
  status: string;
  product_name?: string | null;
  bridge_objective?: string | null;
  product_type?: string | null;
  demand_pattern?: string | null;
  supply_model?: string | null;
  delivery_complexity?: string | null;
  constraint_type?: string | null;
  constraint_severity?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductDomainQuestion {
  question_id: string;
  domain: string;
  factor: string;
  topic?: string;
  text: string;
  options: Array<{ score: number; label: string }>;
  current_answer: { score: number } | null;
}

export interface ProductDomainEntropyMap {
  session_id: string;
  subdomains: Record<string, { entropy_score: number }>;
  aggregate_entropy_score: number;
  highest_entropy_subdomain: string | null;
  bridge_focus?: string | null;
  marketfit_deep_dive_recommended?: boolean;
  correction_factors?: { RCF: number; ECF: number };
  value_ratio: number;
  corrected_value_ratio: number;
  metrics?: {
    current_revenue: number;
    current_expense: number;
    ideal_revenue: number;
    ideal_expense: number;
    recoverable_revenue: number;
    recoverable_expense: number;
    rcf_current?: number;
    ecf_current?: number;
  };
  hierarchy: Array<{
    domain_id: string;
    domain_name: string;
    rollup_entropy_score?: number;
    subdomains: Array<{
      subdomain_id: string;
      subdomain_name: string;
      artifact_item_id: string;
      entropy_score: number;
    }>;
  }>;
  development?: {
    sequencing_entropy_score: number;
    customer_value_priority_score: number;
  };
}

export interface ProductDomainControlBridge {
  session_id: string;
  metrics: {
    value_ratio_current: number;
    value_ratio_ideal: number;
    value_ratio_future: number;
    current_revenue: number;
    current_expense: number;
    ideal_revenue?: number;
    ideal_expense?: number;
    recoverable_revenue: number;
    recoverable_expense: number;
  };
  entropy: Array<{ scope: string; entropy: number; correction: number }>;
  highest_entropy_subdomain: string | null;
  bridge_card_items: Array<{
    id: string;
    question_id: string;
    reason?: string;
    priority: number;
    future_condition?: string | null;
    future_answer_score?: number | null;
    assumption_text?: string | null;
    plank_description?: string | null;
    resource_assignment?: string | null;
    estimated_duration?: string | null;
  }>;
}

function pdClient() {
  return getDriveFlowHttpClient();
}

function pdBase(): string {
  return getProductDomainSessionsBasePath();
}

/** Throws if no client; outer try/catch maps to [] / null like legacy requireClient. */
function assertPdClient(): AxiosInstance {
  const c = pdClient();
  if (!c) {
    throw new Error('Drive flow HTTP client is not available');
  }
  return c;
}

export async function listProductDomainSessions(
  companyId?: string
): Promise<ProductDomainSession[]> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const params = companyId ? { company_id: companyId } : {};
    const res = await client.get<ProductDomainSession[]>(BASE, { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function getProductDomainConfig(): Promise<ProductDomainConfig | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<ProductDomainConfig>(`${BASE}/config`);
    return res.data;
  } catch {
    return null;
  }
}

export async function getProductDomainPhaseData(
  sessionId: string,
  phaseId?: string
): Promise<{ session_id: string; phase_data: Record<string, PhaseDataItem[]> } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const params = phaseId ? `?phase_id=${encodeURIComponent(phaseId)}` : '';
    const res = await client.get<{ session_id: string; phase_data: Record<string, PhaseDataItem[]> }>(
      `${BASE}/${sessionId}/phase-data${params}`
    );
    return res.data;
  } catch {
    return null;
  }
}

export interface PhaseDataItemSubmit {
  question_id: string;
  value_type: 'text' | 'number' | 'select';
  value_text?: string | null;
  value_number?: number | null;
}

export async function upsertProductDomainPhaseData(
  sessionId: string,
  phaseId: string,
  items: PhaseDataItemSubmit[]
): Promise<{ ok: boolean; phase_id: string } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.post<{ ok: boolean; phase_id: string }>(
      `${BASE}/${sessionId}/phase-data`,
      { phase_id: phaseId, items }
    );
    return res.data;
  } catch {
    return null;
  }
}

export interface ProductDomainFinancialSummary {
  product_id: string;
  current_period_id: string | null;
  current_value_ratio: number;
  baseline_period_id: string | null;
  baseline_value_ratio: number | null;
  future_period_id: string | null;
  future_value_ratio: number | null;
  growth_rate: number | null;
  growth_acceleration: number | null;
  growth_constraint_flag: string | null;
  derived_from: Record<string, unknown[]>;
}

export async function getProductDomainFinancialSummary(
  sessionId: string
): Promise<{ session_id: string; financial_summary: ProductDomainFinancialSummary | null } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<{
      session_id: string;
      financial_summary: ProductDomainFinancialSummary | null;
    }>(`${BASE}/${sessionId}/financial-summary`);
    return res.data;
  } catch {
    return null;
  }
}

export interface CreateProductDomainSessionPayload {
  company_id: string;
  user_id: string;
  objective_id?: string | null;
  bridge_session_id?: string | null;
  product_revenue?: number;
  product_expense?: number;
  product_state?: string;
  product_name?: string | null;
  bridge_objective?: string | null;
}

export async function createProductDomainSession(
  payload: CreateProductDomainSessionPayload
): Promise<ProductDomainSession> {
  const client = assertPdClient();
  const BASE = pdBase();
  const res = await client.post<ProductDomainSession>(BASE, {
    company_id: payload.company_id,
    user_id: payload.user_id,
    objective_id: payload.objective_id ?? null,
    bridge_session_id: payload.bridge_session_id ?? null,
    product_revenue: payload.product_revenue ?? 0,
    product_expense: payload.product_expense ?? 0,
    product_state: payload.product_state ?? 'development',
    product_name: payload.product_name ?? null,
    bridge_objective: payload.bridge_objective ?? null,
  });
  return res.data;
}

export async function getProductDomainSession(
  sessionId: string
): Promise<ProductDomainSession | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<ProductDomainSession>(`${BASE}/${sessionId}`);
    return res.data;
  } catch {
    return null;
  }
}

export interface ProductDomainSessionUpdatePayload {
  product_revenue?: number;
  product_expense?: number;
  product_state?: string;
  status?: string;
  product_name?: string | null;
  bridge_objective?: string | null;
  product_type?: string | null;
  demand_pattern?: string | null;
  supply_model?: string | null;
  delivery_complexity?: string | null;
  constraint_type?: string | null;
  constraint_severity?: string | null;
}

export async function updateProductDomainSession(
  sessionId: string,
  payload: ProductDomainSessionUpdatePayload
): Promise<ProductDomainSession | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.patch<ProductDomainSession>(`${BASE}/${sessionId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function getProductDomainQuestions(
  sessionId: string,
  phase: 'current' | 'future' | 'entropy_assessment' = 'current'
): Promise<ProductDomainQuestion[]> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<ProductDomainQuestion[]>(
      `${BASE}/${sessionId}/questions?phase=${phase}`
    );
    return res.data ?? [];
  } catch {
    return [];
  }
}

export async function getProductDomainNextQuestion(
  sessionId: string,
  phase: 'current' | 'future' = 'current'
): Promise<{
  question_id: string;
  domain: string;
  factor: string;
  text: string;
  options: Array<{ score: number; label: string }>;
  current_answer: null;
} | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get(
      `${BASE}/${sessionId}/next-question?phase=${phase}`
    );
    return res.data ?? null;
  } catch {
    return null;
  }
}

export async function submitProductDomainAnswers(
  sessionId: string,
  phase: 'current' | 'future',
  answers: Array<{ question_id: string; score: number }>
): Promise<{ ok: boolean; count: number } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.post(`${BASE}/${sessionId}/answers`, {
      phase,
      answers,
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function calculateProductDomainSession(
  sessionId: string
): Promise<Record<string, unknown> | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.post<Record<string, unknown>>(
      `${BASE}/${sessionId}/calculate`
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getProductDomainEntropyMap(
  sessionId: string
): Promise<ProductDomainEntropyMap | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<ProductDomainEntropyMap>(
      `${BASE}/${sessionId}/entropy-map`
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getProductDomainFriction(
  sessionId: string,
  limit = 10
): Promise<Array<{
  question_id: string;
  score: number;
  base_friction: number;
  adjusted_friction: number;
  text: string;
  domain: string;
  factor: string;
}>> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get(
      `${BASE}/${sessionId}/friction?limit=${limit}`
    );
    return res.data ?? [];
  } catch {
    return [];
  }
}

export async function getProductDomainBridgeCardRecommendations(
  sessionId: string
): Promise<{ candidates: Array<{ question_id: string; score?: number; reason?: string }> } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.post<{ candidates: Array<{ question_id: string; score?: number; reason?: string }> }>(
      `${BASE}/${sessionId}/bridge-card/recommendations`
    );
    return res.data;
  } catch {
    return null;
  }
}

export interface ProductDomainBridgeCardItem {
  id: string;
  session_id: string;
  question_id: string;
  reason?: string;
  priority: number;
  changeable: boolean;
  created_at: string;
  future_condition?: string | null;
  future_answer_score?: number | null;
  assumption_text?: string | null;
  plank_description?: string | null;
  resource_assignment?: string | null;
  estimated_duration?: string | null;
}

export async function getProductDomainBridgeCard(
  sessionId: string
): Promise<ProductDomainBridgeCardItem[]> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get(`${BASE}/${sessionId}/bridge-card`);
    return res.data ?? [];
  } catch {
    return [];
  }
}

export async function setProductDomainBridgeCard(
  sessionId: string,
  items: Array<{ question_id: string; priority?: number; reason?: string }>
): Promise<{ ok: boolean } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.post(`${BASE}/${sessionId}/bridge-card`, { items });
    return res.data;
  } catch {
    return null;
  }
}

export interface ProductDomainBridgeCardDetailsInput {
  question_id: string;
  future_condition?: string | null;
  future_answer_score?: number | null;
  assumption_text?: string | null;
  plank_description?: string | null;
  resource_assignment?: string | null;
  estimated_duration?: string | null;
  resource_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  duration?: string | null;
  remaining_duration?: string | null;
}

export async function updateProductDomainBridgeCardDetails(
  sessionId: string,
  items: ProductDomainBridgeCardDetailsInput[]
): Promise<{ ok: boolean } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.patch(`${BASE}/${sessionId}/bridge-card/details`, {
      items,
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function getProductDomainControlBridge(
  sessionId: string
): Promise<ProductDomainControlBridge | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<ProductDomainControlBridge>(
      `${BASE}/${sessionId}/control-bridge`
    );
    return res.data;
  } catch {
    return null;
  }
}

// ----- Demand Funnel -----

export interface DemandFunnelStage {
  id: string;
  config_id: string;
  name: string;
  display_order: number;
  color: string | null;
}

export interface DemandFunnelEdge {
  id: string;
  config_id: string;
  from_stage_id: string;
  to_stage_id: string;
  conversion_rate: number | null;
  lead_time_days: number | null;
}

export interface DemandFunnelConfig {
  id: string;
  product_domain_session_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface DemandFunnelResponse {
  session_id: string;
  config: DemandFunnelConfig | null;
  stages: DemandFunnelStage[];
  edges: DemandFunnelEdge[];
  counts: Record<string, number>;
}

export interface DemandFunnelStageInput {
  id?: string;
  name: string;
  display_order?: number;
  color?: string | null;
}

export interface DemandFunnelEdgeInput {
  from_stage_id?: string;
  to_stage_id?: string;
  from_stage_index?: number;
  to_stage_index?: number;
  conversion_rate?: number | null;
  lead_time_days?: number | null;
}

export interface DemandFunnelPutPayload {
  name?: string;
  stages: DemandFunnelStageInput[];
  edges: DemandFunnelEdgeInput[];
}

export interface DemandFunnelCountsPayload {
  period_id?: string;
  counts: Array<{ stage_id: string; count: number }>;
}

export async function getDemandFunnel(
  sessionId: string
): Promise<DemandFunnelResponse | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<DemandFunnelResponse>(
      `${BASE}/${sessionId}/demand-funnel`
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function putDemandFunnel(
  sessionId: string,
  body: DemandFunnelPutPayload
): Promise<DemandFunnelResponse | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.put<DemandFunnelResponse>(
      `${BASE}/${sessionId}/demand-funnel`,
      body
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function setDemandFunnelCounts(
  sessionId: string,
  body: DemandFunnelCountsPayload
): Promise<{ ok: boolean; period_id?: string } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.post<{ ok: boolean; period_id?: string }>(
      `${BASE}/${sessionId}/demand-funnel/counts`,
      body
    );
    return res.data;
  } catch {
    return null;
  }
}

// ----- Product Pipeline -----

export type PipelineSubdomain = 'demand' | 'supply' | 'delivery';

export interface PipelineStage {
  id: string;
  config_id: string;
  subdomain: PipelineSubdomain;
  name: string;
  display_order: number;
  color: string | null;
}

export interface PipelineEdge {
  id: string;
  config_id: string;
  from_stage_id: string;
  to_stage_id: string;
  conversion_rate: number | null;
  lead_time_days: number | null;
}

export interface PipelineConfig {
  id: string;
  product_domain_session_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface PipelineResponse {
  session_id: string;
  config: PipelineConfig | null;
  stages: PipelineStage[];
  edges: PipelineEdge[];
  counts: Record<string, number>;
}

export interface PipelineStageInput {
  id?: string;
  subdomain: PipelineSubdomain;
  name: string;
  display_order?: number;
  color?: string | null;
}

export interface PipelineEdgeInput {
  from_stage_id?: string;
  to_stage_id?: string;
  from_stage_index?: number;
  to_stage_index?: number;
  conversion_rate?: number | null;
  lead_time_days?: number | null;
}

export interface PipelinePutPayload {
  name?: string;
  stages: PipelineStageInput[];
  edges: PipelineEdgeInput[];
}

export interface PipelineCountsPayload {
  period_id?: string;
  counts: Array<{ stage_id: string; count: number }>;
}

export async function getPipeline(
  sessionId: string
): Promise<PipelineResponse | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.get<PipelineResponse>(
      `${BASE}/${sessionId}/pipeline`
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function putPipeline(
  sessionId: string,
  body: PipelinePutPayload
): Promise<PipelineResponse | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.put<PipelineResponse>(
      `${BASE}/${sessionId}/pipeline`,
      body
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function setPipelineCounts(
  sessionId: string,
  body: PipelineCountsPayload
): Promise<{ ok: boolean; period_id?: string } | null> {
  try {
    const client = assertPdClient();
    const BASE = pdBase();
    const res = await client.post<{ ok: boolean; period_id?: string }>(
      `${BASE}/${sessionId}/pipeline/counts`,
      body
    );
    return res.data;
  } catch {
    return null;
  }
}
